import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalToast } from '@/contexts/ToastContext';
import { useAuthStore } from '@/store/authStore';
import { CheckCircle, ArrowRight, ArrowLeft, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { CompanyContactInfoStep } from '@/components/profile-wizard/CompanyContactInfoStep';
import { CompanyBusinessDetailsStep } from '@/components/profile-wizard/CompanyBusinessDetailsStep';
import { PortfolioStep } from '@/components/profile-wizard/PortfolioStep';
import { CompanyInsuranceStep } from '@/components/profile-wizard/CompanyInsuranceStep';
import { QualificationsStep } from '@/components/profile-wizard/QualificationsStep';
import { ServicesPricingStep } from '@/components/profile-wizard/ServicesPricingStep';

const STEPS = [
  { id: 1, name: 'Company Contact', key: 'contact' },
  { id: 2, name: 'Business & Legal', key: 'business' },
  { id: 3, name: 'Portfolio (Optional)', key: 'portfolio' },
  { id: 4, name: 'Insurance & Certs', key: 'insurance' },
  { id: 5, name: 'Services & Pricing', key: 'services' }
];

export function CompanyProfileWizard() {
  const { success, error } = useGlobalToast();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [profileData, setProfileData] = useState<any>({});
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadProfileData();
  }, []);

  const loadProfileData = async () => {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://npm.rapidfix.ie';
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/providers/profile`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        const loadedProfile = data.profile_data || {};
        
        // Pre-populate company data
        if (!loadedProfile.businessName && user?.businessName) {
          loadedProfile.businessName = user.businessName;
        }
        if (!loadedProfile.email && user?.email) {
          loadedProfile.email = user.email;
        }
        if (!loadedProfile.phone && user?.phone) {
          loadedProfile.phone = user.phone;
        }
        
        setProfileData(loadedProfile);
        
        let completedSteps = data.completed_steps || [];
        if (Array.isArray(completedSteps)) {
          completedSteps = completedSteps.filter((step: number) => step <= 5);
        }
        setCompletedSteps(completedSteps);
        
        const loadedStep = data.current_step || 1;
        setCurrentStep(loadedStep > 5 ? 5 : loadedStep);
      }
    } catch (err) {
      console.error('Failed to load profile:', err);
      if (user) {
        setProfileData({
          businessName: user.businessName || '',
          email: user.email || '',
          phone: user.phone || ''
        });
      }
    }
  };

  const validateStep = (stepNumber: number): { valid: boolean; message?: string } => {
    switch (stepNumber) {
      case 1: // Company Contact
        if (!profileData.phone?.trim()) return { valid: false, message: 'Company phone is required' };
        if (!profileData.businessLocation?.trim()) return { valid: false, message: 'Business location is required' };
        return { valid: true };
      
      case 2: // Business Details & Legal
        if (!profileData.businessName?.trim()) return { valid: false, message: 'Business name is required' };
        if (!profileData.yearsInBusiness) return { valid: false, message: 'Years in business is required' };
        if (!profileData.bio?.trim()) return { valid: false, message: 'Business description is required' };
        return { valid: true };
      
      case 3: // Portfolio - OPTIONAL
        return { valid: true };
      
      case 4: // Insurance & Certifications - OPTIONAL
        return { valid: true };
      
      case 5: // Services & Pricing
        if (!profileData.serviceCounties || profileData.serviceCounties.length === 0) {
          return { valid: false, message: 'Please select at least one service area (county)' };
        }
        if (!profileData.specializations || profileData.specializations.length === 0) {
          return { valid: false, message: 'Please select at least one service category' };
        }
        return { valid: true };
      
      default:
        return { valid: true };
    }
  };

  const saveProgress = async (asDraft: boolean = false) => {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://npm.rapidfix.ie';
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/providers/profile/wizard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ 
          current_step: currentStep,
          completed_steps: completedSteps,
          profile_data: profileData,
          status: 'draft'
        })
      });
      
      if (response.ok) {
        if (asDraft) {
          success('Draft Saved', 'Your changes have been saved as draft');
        }
        return true;
      } else {
        error('Save Failed', 'Could not save your progress');
        return false;
      }
    } catch (err) {
      error('Save Failed', 'Could not save your progress');
      return false;
    }
  };

  const handleNext = async () => {
    const validation = validateStep(currentStep);
    if (!validation.valid) {
      error('Incomplete Information', validation.message || 'Please fill in all required fields before continuing');
      return;
    }

    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps([...completedSteps, currentStep]);
    }

    const saved = await saveProgress(false);
    if (saved && currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = async () => {
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps([...completedSteps, currentStep]);
    }
    
    const finalCompletedSteps = completedSteps.includes(currentStep) 
      ? completedSteps 
      : [...completedSteps, currentStep];

    setIsSubmitting(true);
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://npm.rapidfix.ie';
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/providers/profile/wizard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          current_step: currentStep,
          completed_steps: finalCompletedSteps,
          profile_data: profileData,
          status: 'draft'
        })
      });
      
      if (response.ok) {
        success('Profile Saved!', 'Your company profile has been updated successfully');
        navigate('/profile/company');
      } else {
        const data = await response.json();
        error('Save Failed', data.error || 'Could not save your profile');
      }
    } catch (err) {
      error('Save Failed', 'Could not save your profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateProgress = () => {
    return Math.round((completedSteps.length / STEPS.length) * 100);
  };

  const renderStep = () => {
    const props = {
      data: profileData,
      onChange: setProfileData
    };

    switch (currentStep) {
      case 1: return <CompanyContactInfoStep {...props} />;
      case 2: return <CompanyBusinessDetailsStep {...props} />;
      case 3: return <PortfolioStep {...props} />;
      case 4: return (
        <>
          <CompanyInsuranceStep {...props} />
          <div className="border-t mt-6 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Certifications</h3>
            <QualificationsStep {...props} />
          </div>
        </>
      );
      case 5: return <ServicesPricingStep {...props} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch">
        Skip to main content
      </a>
      <main id="main-content" role="main" className="max-w-4xl mx-auto px-4">
        {/* Progress Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Complete Your Company Profile</h1>
              <p className="text-gray-600">Set up your company profile to get verified and start receiving jobs</p>
            </div>
            <Button variant="outline" onClick={() => navigate('/profile/company')}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                Step {currentStep} of {STEPS.length}
              </span>
              <span className="text-sm font-medium text-primary-600">
                {calculateProgress()}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${calculateProgress()}%` }}
              />
            </div>
          </div>

          {/* Step Indicators */}
          <div className="flex justify-between mt-6">
            {STEPS.map((step) => (
              <button
                key={step.id}
                type="button"
                onClick={() => setCurrentStep(step.id)}
                className="flex flex-col items-center flex-1 group cursor-pointer hover:scale-105 transition-transform"
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                  completedSteps.includes(step.id)
                    ? 'bg-emerald-500 border-emerald-500 text-white group-hover:bg-emerald-600'
                    : currentStep === step.id
                    ? 'bg-primary-600 border-primary-600 text-white group-hover:bg-primary-700'
                    : 'bg-white border-gray-300 text-gray-400 group-hover:border-primary-400'
                }`}>
                  {completedSteps.includes(step.id) ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-bold">{step.id}</span>
                  )}
                </div>
                <span className={`text-xs mt-2 text-center transition-colors ${
                  currentStep === step.id 
                    ? 'text-gray-900 font-medium' 
                    : 'text-gray-500 group-hover:text-primary-600'
                }`}>
                  {step.name}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <Card className="p-8 mb-6">
          {renderStep()}
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 1}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>

          {currentStep < 5 ? (
            <Button onClick={handleNext}>
              <Save className="w-4 h-4 mr-2" />
              Save & Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleFinish}
              disabled={isSubmitting}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Saving...' : 'Save & Finish'}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
